<template>
  <!--
    Custom Deck brand-fields form. Renders one of two views over the
    same shared brand-state object:

      mode="simple"   — Deck name + Theme (combobox) + Generation
                        prompt base (textarea). The two fields that
                        actually drive output + filtering. Default.

      mode="advanced" — All 7 brand fields as comboboxes
                        (theme/style/mood/color_palette/motif/era/
                        energy) + Generation prompt base. Mirrors the
                        MCP deck-creation form.

    Both modes bind to the parent's ``modelValue`` so toggling tabs
    keeps state. Aida's chat-action handler also writes into the same
    object — Simple/Advanced/Aida are three views of one truth.

    Each combobox uses HTML ``<input list="...">`` + ``<datalist>``,
    which gives type-or-pick behavior with no JS dropdown logic.
    Operators curate the preset values via the MCP page at
    /mcp/custom-deck-presets/.
  -->
  <form class="brand-form" @submit.prevent="$emit('submit', value)">
    <!-- Help opt-in — opens the studio AI chat panel where Aida can
         help draft the brand. Hidden when the parent says we're not
         in a "Build new deck" context (e.g., post-start phase where
         the form just shows the saved brand for reference). -->
    <button
      v-if="showHelpIcon"
      type="button"
      class="help-icon-btn"
      @click="onHelpClick"
      aria-label="Get help filling this out from Mystic AIda"
    >
      <span class="help-glyph">?</span>
      <span class="help-label">Help me fill this out</span>
    </button>

    <div class="row">
      <label>
        <span class="label">
          Deck name
          <FieldHelpIcon
            field="deck_name" label="Deck name" panel="custom-deck"
            :value="value.name"
            title="Help me name this deck with Mystic Aida"
          />
        </span>
        <input
          v-model="value.name"
          type="text"
          maxlength="80"
          placeholder="e.g., Cyberpunk Noir"
          required
          @blur="onFieldBlur('deck_name', 'Deck name', value.name)"
        />
      </label>
    </div>

    <!-- Simple: theme combobox only -->
    <div v-if="mode === 'simple'" class="single">
      <label>
        <span class="label">
          Theme
          <FieldHelpIcon
            field="theme" label="Theme" panel="custom-deck"
            :value="value.theme"
            title="Help me pick a theme with Mystic Aida"
          />
        </span>
        <input
          v-model="value.theme"
          type="text"
          list="presets-theme"
          placeholder="cyberpunk, cottagecore, celestial…"
          @blur="onFieldBlur('theme', 'Theme', value.theme)"
        />
        <small class="hint">
          Pick a preset or type your own. Aida can also help -
          describe the deck in chat and she'll fill this in.
        </small>
      </label>
    </div>

    <!-- Advanced: full 7-field grid. Each field is its own facet of
         the deck's brand and can be confusing in isolation, so every
         one gets its own help icon. -->
    <div v-else class="grid">
      <label v-for="f in advancedFields" :key="f.key">
        <span class="label">
          {{ f.label }}
          <FieldHelpIcon
            :field="f.key" :label="f.label" panel="custom-deck"
            :value="value[f.key]"
            :title="`Help me with ${f.label} from Mystic Aida`"
          />
        </span>
        <input
          v-model="value[f.key]"
          type="text"
          :list="`presets-${f.key}`"
          :placeholder="f.placeholder"
          @blur="onFieldBlur(f.key, f.label, value[f.key])"
        />
      </label>
    </div>

    <label class="full">
      <span class="label">
        Generation prompt base
        <FieldHelpIcon
          field="generation_prompt_base" label="Generation prompt base" panel="custom-deck"
          :value="value.generation_prompt_base"
          title="Help me write the generation prompt base with Mystic Aida"
        />
      </span>
      <textarea
        v-model="value.generation_prompt_base"
        rows="4"
        placeholder="Describe rendering style, palette, lighting, recurring motifs. NO card-specific imagery - every card uses this as the base prompt."
        @blur="onFieldBlur('generation_prompt_base', 'Generation prompt base', value.generation_prompt_base)"
      ></textarea>
      <small class="hint">
        This goes to the image model on every card. Keep it about
        rendering, not card content. The most load-bearing field.
      </small>
    </label>

    <div class="actions">
      <button
        type="submit"
        class="primary-btn"
        :disabled="submitting || !value.name?.trim()"
      >{{ submitting ? 'Saving…' : submitLabel }}</button>
      <button
        v-if="showCancel"
        type="button"
        class="ghost-btn"
        @click="$emit('cancel')"
        :disabled="submitting"
      >Cancel</button>
    </div>

    <p v-if="error" class="error">⚠ {{ error }}</p>

    <!--
      Datalists — rendered once for all 7 fields so both Simple and
      Advanced modes can reference them. Datalists with no options
      simply suggest nothing; harmless when a field has no presets.
    -->
    <datalist
      v-for="f in advancedFields"
      :key="`dl-${f.key}`"
      :id="`presets-${f.key}`"
    >
      <option
        v-for="opt in (presets?.[f.key] || [])"
        :key="opt"
        :value="opt"
      />
    </datalist>
  </form>
</template>

<script setup>
import { reactive, watch, onMounted, onUnmounted } from 'vue'
import FieldHelpIcon from '../../FieldHelpIcon.vue'
import { dispatchFieldContext } from '@/utils/studio-assistant'

// Blur on any ``?``-equipped field updates the assistant's active
// field. ReaderPage gates open-on-blur to "panel already open" so
// tabbing through the brand grid doesn't pop the assistant.
function onFieldBlur(field, label, val) {
  dispatchFieldContext({ field, label, panel: 'custom-deck', value: val, source: 'blur' })
}

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  // Operator-curated presets keyed by field_name. Shape:
  //   { theme: ['cyberpunk', 'cottagecore', ...], style: [...], ... }
  presets: { type: Object, default: () => ({}) },
  mode: {
    type: String,
    default: 'simple',
    validator: v => ['simple', 'advanced'].includes(v),
  },
  submitting: { type: Boolean, default: false },
  submitLabel: { type: String, default: 'Start this deck' },
  showCancel: { type: Boolean, default: true },
  error: { type: String, default: '' },
  // Show the "Help me fill this out" icon above the form. The parent
  // (CustomDeckBuilder) sets this true on the no_draft "Build new"
  // screen and false everywhere else.
  showHelpIcon: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'submit', 'cancel'])

function onHelpClick() {
  // Whole-form help: ask ReaderPage to open the assistant, show the
  // "Helping with" pill, and seed a short description of how to fill the
  // deck brief. Fired ONLY on this explicit click — the form never
  // auto-introduces on mode entry (that was the spammy greeting).
  window.dispatchEvent(new CustomEvent('custom-deck:help'))
}

const advancedFields = [
  { key: 'theme', label: 'Theme', placeholder: 'cyberpunk' },
  { key: 'style', label: 'Style', placeholder: 'neon noir' },
  { key: 'mood', label: 'Mood', placeholder: 'urgent, electric' },
  { key: 'color_palette', label: 'Color palette', placeholder: 'magenta, cyan, jet black' },
  { key: 'motif', label: 'Motif', placeholder: 'circuitry' },
  { key: 'era', label: 'Era', placeholder: 'near-future' },
  { key: 'energy', label: 'Energy', placeholder: 'kinetic' },
]

// Mirror the parent's modelValue locally so v-model wiring works
// whether Aida fills fields async or the operator types directly.
const value = reactive({
  name: '',
  theme: '',
  style: '',
  mood: '',
  color_palette: '',
  motif: '',
  era: '',
  energy: '',
  generation_prompt_base: '',
  ...props.modelValue,
})

// Push edits back up; parent stores the truth.
watch(
  value,
  (v) => emit('update:modelValue', { ...v }),
  { deep: true },
)

// Pull external updates (Aida fills, draft load, mode switch) back
// down without clobbering in-progress local edits to other fields.
watch(
  () => props.modelValue,
  (incoming) => {
    if (!incoming) return
    for (const k of Object.keys(incoming)) {
      if (incoming[k] !== undefined && incoming[k] !== value[k]) {
        value[k] = incoming[k]
      }
    }
  },
  { deep: true },
)

// Write back a polished value from the per-field assistant (the "?" /
// pencil icons). The deck-name field uses the key ``deck_name`` (NOT
// ``name`` — that key is reserved for the reader name-claim flow); map
// it onto our local ``name``. Other field keys match 1:1.
function _onPolishApplied(e) {
  const { field, value: polished } = e.detail || {}
  if (!field || polished == null) return
  const key = field === 'deck_name' ? 'name' : field
  if (key in value) value[key] = polished
}
onMounted(() => window.addEventListener('studio-assistant:polish-applied', _onPolishApplied))
onUnmounted(() => window.removeEventListener('studio-assistant:polish-applied', _onPolishApplied))
</script>

<style scoped>
.brand-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.help-icon-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  background: transparent;
  border: 1px dashed rgba(195, 169, 255, 0.32);
  border-radius: 999px;
  color: rgba(212, 196, 232, 0.78);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.18s;
}
.help-icon-btn:hover {
  border-style: solid;
  border-color: #C9A94E;
  color: #C9A94E;
}
.help-icon-btn .help-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: rgba(201, 169, 78, 0.14);
  border: 1px solid rgba(201, 169, 78, 0.45);
  border-radius: 50%;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  color: #C9A94E;
}
.row, .full, .single { display: flex; flex-direction: column; }
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.label {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.7);
}
input, textarea {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
  color: #f3e9d2;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.18s;
}
input:focus, textarea:focus { border-color: #C9A94E; }
textarea { resize: vertical; min-height: 88px; }
.hint {
  color: rgba(212, 196, 232, 0.5);
  font-size: 11px;
  font-style: italic;
}
.actions {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}
.primary-btn, .ghost-btn {
  padding: 10px 22px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.primary-btn {
  background: #663399;
  color: #fff;
  border: none;
}
.primary-btn:hover:not(:disabled) { background: #7a3fb1; }
.primary-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.ghost-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
}
.ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 4px 0 0;
}

@media (max-width: 640px) {
  .grid { grid-template-columns: 1fr; }
}
</style>
